import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';
import {
  InviteePayload,
  InviteeResponse,
  ProfileReuseStatus,
  useDeleteInvitee,
  useUpdateInvitee,
} from 'api';
import { Alert, TextV2 } from '@insights-ltd/design-library/components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import DeleteLearnerDialog from 'pages/EventLearners/DeleteLearnerDialog';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import ActionRequiredStatusList from './ActionRequiredStatusList';

type ActionRequiredListProps = {
  invitees: InviteeResponse[];
  eventId: string;
  isPastExperience: boolean;
  eventTimezone: string;
  eventDialect: string;
};

const ActionRequiredList = ({
  invitees,
  eventId,
  isPastExperience,
  eventTimezone,
  eventDialect,
}: ActionRequiredListProps) => {
  const { t } = useTranslation();
  const queryClient: QueryClient = useQueryClient();
  const { mutate: updateInvitee, isError: updateIsError } =
    useUpdateInvitee(queryClient);
  const {
    mutate: deleteInvitee,
    isPending: deleteIsPending,
    isError: deleteIsError,
    reset: deleteReset,
  } = useDeleteInvitee();
  const [inviteeId, setInviteeId] = useState<string>('');
  const inviteesCount: number = invitees.length;

  const handleUpdateInvitee = async (
    profileReusePreference: ProfileReuseStatus,
    invitee: InviteeResponse,
  ) => {
    const updatedInvitee: InviteePayload = {
      ...invitee,
      profileReusePreference,
    };
    updateInvitee({ eventId, invitee: updatedInvitee });
  };

  const handleDeleteInvitee = async (
    selectedInviteeId: string,
  ): Promise<void> => {
    setInviteeId(selectedInviteeId);
  };

  const handleConfirmDelete = () =>
    deleteInvitee(
      { eventId, inviteeId },
      { onSuccess: () => setInviteeId('') },
    );

  return (
    <>
      <TextV2 component="h2" variant="h3">
        {t('ui.event-management.events.action-required-title', {
          count: inviteesCount,
        })}
      </TextV2>
      <Box
        sx={{
          mt: (theme) => theme.spacing(spacingSizeMap.S),
        }}
      />
      <Grid container>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextV2>
            {t('ui.event-management.events.action-required-description')}
          </TextV2>
        </Grid>
      </Grid>
      <Box
        sx={{
          mt: (theme) => theme.spacing(spacingSizeMap.S),
        }}
      />
      <Grid
        container
        sx={(theme) => ({
          padding: `${theme.spacing(spacingSizeMap.XS)} ${theme.spacing(
            spacingSizeMap.S,
          )}`,
          color: theme.palette.grey[700],
          backgroundColor: theme.palette.grey[300],
        })}
      >
        <Grid size={3}>
          <TextV2 variant="bodyBold">
            {t('ui.event-management.events.list.heading.full-name-email')}
          </TextV2>
        </Grid>
        <Grid size={4}>
          <TextV2 variant="bodyBold">
            {t('ui.event-management.events.list.heading.profile-chapters')}
          </TextV2>
        </Grid>
      </Grid>
      <Box
        sx={{
          mt: (theme) => theme.spacing(spacingSizeMap.S),
        }}
      />
      <ActionRequiredStatusList
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        invitees={invitees!}
        updateInvitee={handleUpdateInvitee}
        deleteInvitee={handleDeleteInvitee}
        isPastExperience={isPastExperience}
        eventTimezone={eventTimezone}
        eventDialect={eventDialect}
      />
      <DeleteLearnerDialog
        open={Boolean(inviteeId)}
        onClose={() => setInviteeId('')}
        handleConfirmDelete={handleConfirmDelete}
        deleting={deleteIsPending}
      />
      <Snackbar
        open={deleteIsError}
        autoHideDuration={6000}
        onClose={deleteReset}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={deleteReset}
          severity="error"
        >
          {t('ui.event-management.events.invitee.error-deleting-invitee')}
        </Alert>
      </Snackbar>
      <Snackbar open={updateIsError} autoHideDuration={6000}>
        <Alert elevation={6} variant="filled" severity="error">
          {t('ui.event-management.events.invitee.error-updating-invitee')}
        </Alert>
      </Snackbar>
      <Box
        sx={{
          mt: (theme) => theme.spacing(spacingSizeMap.L),
        }}
      />
    </>
  );
};

export default ActionRequiredList;
